/**
 * Düğüncüm — SaaS tanıtım (landing) sitesi. Apple-sade / açık tasarım.
 *
 * Neredeyse monokrom palet, SF Pro tipografisi, bol beyaz alan ve ince,
 * amaçlı scroll animasyonları (ürün scroll ile büyür, ifade metni kelime
 * kelime aydınlanır, sabitlenen ürün + sıralı özellik anlatımı).
 *
 * Fiyatlandırma ödemeye değil WhatsApp'a yönlendirir. Yönetim paneline
 * `/yonetim` adresinden erişilir.
 */
import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import { PLANS, whatsappLink, ADMIN_URL, REGISTER_URL, WHATSAPP_DISPLAY, CONTACT_EMAIL, APP_STORE_URL, PLAY_STORE_URL } from './config';
import './marketing.css';
import { Icon, IconName, Reveal, CountUp, WhatsAppIcon } from './ui';

const LOGO = '/media/logos/logo.png';

// Gerçek yönetim panelinden alınmış ekran görüntüleri (demo verilerle).
const SHOTS = {
  takvim: '/media/screenshots/panel-takvim.png',
  musteriler: '/media/screenshots/panel-musteriler.png',
  kasa: '/media/screenshots/panel-kasa.png',
  mobil: '/media/screenshots/mobil-takvim.png',
};

const Chevron: React.FC = () => (
  <svg
    className="dg-chev"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 4l4 4-4 4" />
  </svg>
);

const AppleIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.05 12.54c-.03-2.5 2.04-3.7 2.13-3.76-1.16-1.7-2.97-1.93-3.61-1.96-1.54-.16-3 .9-3.78.9-.78 0-1.98-.88-3.25-.86-1.67.02-3.21.97-4.07 2.47-1.73 3-.44 7.44 1.24 9.87.82 1.19 1.8 2.53 3.08 2.48 1.24-.05 1.71-.8 3.21-.8 1.49 0 1.92.8 3.23.77 1.33-.02 2.18-1.21 3-2.41.94-1.38 1.33-2.72 1.35-2.79-.03-.01-2.59-.99-2.62-3.94zM14.6 5.1c.69-.83 1.15-1.99 1.02-3.14-.99.04-2.19.66-2.9 1.49-.64.73-1.2 1.9-1.05 3.02 1.1.09 2.24-.56 2.93-1.37z" />
  </svg>
);

const PlayIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3.6 2.3c-.24.25-.38.64-.38 1.14v17.12c0 .5.14.89.39 1.14l.06.05L13.2 12.1v-.22L3.66 2.25l-.06.05z" fill="#00d3ff" />
    <path d="M16.4 15.3l-3.2-3.2v-.22l3.2-3.2.07.04 3.79 2.15c1.08.61 1.08 1.62 0 2.24l-3.79 2.15-.07.04z" fill="#ffce00" />
    <path d="M16.47 15.26L13.2 11.99l-9.6 9.65c.36.38.94.42 1.6.05l11.27-6.43z" fill="#ff3d47" />
    <path d="M16.47 8.72L5.2 2.29c-.66-.37-1.24-.33-1.6.05l9.6 9.65 3.27-3.27z" fill="#00f076" />
  </svg>
);

/* ==========================================================================
 * Navigasyon
 * ========================================================================== */
const NAV_LINKS = [
  { label: 'Genel Bakış', href: '#genel' },
  { label: 'Özellikler', href: '#ozellikler' },
  { label: 'Mobil', href: '#mobil' },
  { label: 'Fiyatlandırma', href: '#fiyatlandirma' },
  { label: 'SSS', href: '#sss' },
];

const Nav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`dg-nav ${scrolled ? 'dg-nav--scrolled' : ''}`}>
        <div className="dg-container dg-nav__inner">
          <a className="dg-nav__brand" href="#top">
            <img src={LOGO} alt="Düğüncüm" />
          </a>
          <div className="dg-nav__links">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href}>
                {l.label}
              </a>
            ))}
          </div>
          <div className="dg-nav__actions">
            <a className="dg-nav__login" href={ADMIN_URL}>
              Giriş
            </a>
            <a className="dg-btn dg-btn--primary dg-nav__cta" href={REGISTER_URL}>
              Ücretsiz Dene
            </a>
            <button className="dg-nav__burger" aria-label="Menü" onClick={() => setMenuOpen((v) => !v)}>
              <span style={menuOpen ? { transform: 'translateY(6.5px) rotate(45deg)' } : undefined} />
              <span style={menuOpen ? { opacity: 0 } : undefined} />
              <span style={menuOpen ? { transform: 'translateY(-6.5px) rotate(-45deg)' } : undefined} />
            </button>
          </div>
        </div>
      </nav>
      <AnimatePresence>
        {menuOpen && (
          <motion.div className="dg-mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.22 }}>
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}>
                {l.label}
              </a>
            ))}
            <a href={ADMIN_URL} onClick={() => setMenuOpen(false)}>
              Yönetim Paneli
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ==========================================================================
 * Hero — ürün scroll ile büyür
 * ========================================================================== */
const Hero: React.FC = () => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const stageScale = useTransform(scrollYProgress, [0, 0.6], [0.94, 1.02]);
  const contentY = useTransform(scrollYProgress, [0, 0.6], [0, -40]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.55], [1, 0]);

  return (
    <header className="dg-hero" id="top" ref={ref}>
      <div className="dg-container">
        <motion.div className="d-flex flex-column align-items-center justify-content-center" style={{ y: contentY, opacity: contentOpacity }}>
          <motion.p className="dg-hero__eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            Düğüncüm
          </motion.p>
          <motion.h1 className="dg-display dg-hero__title" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.08 }}>
            Düğün yönetimi.
            <br />
            <span className="dg-soft">Sadeleştirildi.</span>
          </motion.h1>
          <motion.p className="d-flex flex-column  dg-lead dg-hero__sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.2 }}>
            Rezervasyon, tahsilat, personel ve raporlama. İşletmenizin tüm organizasyon süreci tek, sade bir panelde.
          </motion.p>
          <motion.div className="dg-hero__actions" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.32 }}>
            <a className="dg-btn dg-btn--primary dg-btn--lg" href={REGISTER_URL}>
              Ücretsiz başla
            </a>
            <a className="dg-link" href="#genel">
              Nasıl çalışır <Chevron />
            </a>
          </motion.div>
          <motion.p className="dg-hero__note" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.45 }}>
            1 ay ücretsiz · Kredi kartı gerekmez
          </motion.p>
        </motion.div>

        <motion.div className="dg-hero__stage" style={{ scale: stageScale }}>
          <HeroMock />
        </motion.div>
      </div>
    </header>
  );
};

const HeroMock: React.FC = () => (
  <div className="dg-mock">
    <div className="dg-mock__bar">
      <span className="dg-mock__dot" />
      <span className="dg-mock__dot" />
      <span className="dg-mock__dot" />
      <span className="dg-mock__url">duguncum.com/panel</span>
    </div>
    <img className="dg-mock__shot" src={SHOTS.takvim} alt="Düğüncüm yönetim paneli — rezervasyon takvimi" />
  </div>
);

/* ==========================================================================
 * Büyük ifade — kelimeler scroll ile aydınlanır
 * ========================================================================== */
const STATEMENT = 'Dağınık defterler, kaybolan ödemeler ve unutulan randevular yerine; her şeyin yerli yerinde olduğu tek bir akıllı sistem.';

const Statement: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const words = STATEMENT.split(' ');
  const [lit, setLit] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.85', 'end 0.35'] });

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setLit(Math.round(v * words.length));
  });

  return (
    <section className="dg-statement" id="genel" ref={ref}>
      <div className="dg-container">
        <p className="dg-statement__text">
          {words.map((w, i) => (
            <span key={i} className="dg-statement__word" style={{ color: i < lit ? 'var(--ink)' : 'var(--line)', transition: 'color 0.3s ease' }}>
              {w}{' '}
            </span>
          ))}
        </p>
      </div>
    </section>
  );
};

/* ==========================================================================
 * Sabitlenen ürün + sıralı özellik anlatımı
 * ========================================================================== */
type Feature = { icon: IconName; title: string; text: string; shot: string; url: string };
const FEATURES: Feature[] = [
  {
    icon: 'calendar',
    title: 'Rezervasyon & takvim',
    text: 'Gelen taleplerden onaylı rezervasyonlara kadar tüm süreci tek takvimde yönetin. Tarih çakışmalarını ve doluluğu anında görün.',
    shot: SHOTS.takvim,
    url: 'duguncum.com/panel',
  },
  {
    icon: 'users',
    title: 'Müşteri & satış yönetimi',
    text: 'Tüm rezervasyonlar, müşteri bilgileri ve organizasyon detayları tek listede. Filtreleyin, düzenleyin, Excel’e aktarın.',
    shot: SHOTS.musteriler,
    url: 'duguncum.com/musterilerim',
  },
  {
    icon: 'wallet',
    title: 'Finans, kasa & tahsilat',
    text: 'Peşinat, taksit ve giderler otomatik takip edilir; kasa bakiyeniz her an günceldir. Ay sonu sürprizi olmaz.',
    shot: SHOTS.kasa,
    url: 'duguncum.com/kasalar',
  },
];

const PinnedFeatures: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setActive(Math.min(FEATURES.length - 1, Math.max(0, Math.floor(v * FEATURES.length))));
  });
  const cur = FEATURES[active];

  return (
    <section className="dg-section dg-pinned" id="ozellikler">
      <div className="dg-container">
        <div className="dg-section__head">
          <span className="dg-eyebrow">Tek panel</span>
          <h2 className="dg-h2">İşletmenizin her adımı için.</h2>
          <p className="dg-lead">Operasyonun tamamı, öğrenmesi kolay tek bir akışta. (Gerçek panel görüntüleri.)</p>
        </div>

        <div className="dg-pinned__wrap" ref={ref}>
          <div className="dg-pinned__sticky">
            <div className="dg-pinned__frame">
              <div className="dg-mock__bar">
                <span className="dg-mock__dot" />
                <span className="dg-mock__dot" />
                <span className="dg-mock__dot" />
                <AnimatePresence mode="wait">
                  <motion.span key={active} className="dg-mock__url" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
                    {cur.url}
                  </motion.span>
                </AnimatePresence>
              </div>
              <div className="dg-pinned__shotwrap">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={cur.shot}
                    className="dg-pinned__shot"
                    alt={cur.title}
                    initial={{ opacity: 0, scale: 1.01 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  />
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="dg-pinned__steps">
            {FEATURES.map((f, i) => (
              <div key={f.title} className={`dg-feature ${i === active ? 'is-active' : ''}`}>
                <div className="dg-feature__num">0{i + 1}</div>
                <h3 className="dg-feature__title">{f.title}</h3>
                <p className="dg-feature__text">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ==========================================================================
 * Bento grid
 * ========================================================================== */
const Bento: React.FC = () => (
  <section className="dg-section">
    <div className="dg-container">
      <div className="dg-section__head">
        <span className="dg-eyebrow">Neden Düğüncüm</span>
        <h2 className="dg-h2">Sadece yönetim değil, tam bir deneyim.</h2>
      </div>

      <div className="dg-bento">
        <Reveal className="dg-tile dg-tile--dark span-3" y={20}>
          <div className="dg-tile__icon">
            <Icon name="calendar" />
          </div>
          <div className="dg-tile__eyebrow">Merkezî takvim</div>
          <div className="dg-tile__title">Tüm rezervasyonlar tek bakışta</div>
          <p className="dg-tile__text">Çakışmasız planlama, ön talepten onaya kadar akıcı bir süreç.</p>
          <div className="dg-tile__spacer" />
          <div className="dg-tile__visual">
            {[40, 70, 55, 90, 62, 78, 48].map((h, i) => (
              <span key={i} className="dg-tile__vbar" style={{ height: `${h}%` }} />
            ))}
          </div>
        </Reveal>

        <Reveal className="dg-tile dg-tile--light span-3" y={20} delay={0.05}>
          <div className="dg-tile__icon">
            <Icon name="wallet" />
          </div>
          <div className="dg-tile__eyebrow">Finansal netlik</div>
          <div className="dg-tile__title">Paranızın nerede olduğunu her an bilin</div>
          <p className="dg-tile__text">Peşinat, taksit ve bakiyeler otomatik takip edilir; ay sonu sürprizi olmaz.</p>
          <span className="dg-tile__pill">₺1,4M bu ay tahsil edildi</span>
        </Reveal>

        <Reveal className="dg-tile dg-tile--light span-2" y={20}>
          <div className="dg-tile__icon">
            <Icon name="bell" />
          </div>
          <div className="dg-tile__title">SMS & bildirim</div>
          <p className="dg-tile__text">Otomatik hatırlatma ve onay mesajları.</p>
        </Reveal>

        <Reveal className="dg-tile dg-tile--blue span-2" y={20} delay={0.05}>
          <div className="dg-tile__icon">
            <Icon name="shield" />
          </div>
          <div className="dg-tile__title">Güvenli & yedekli</div>
          <p className="dg-tile__text">Verileriniz şifreli, %99,9 çalışma süresi.</p>
        </Reveal>

        <Reveal className="dg-tile dg-tile--light span-2" y={20} delay={0.1}>
          <div className="dg-tile__icon">
            <Icon name="sparkles" />
          </div>
          <div className="dg-tile__title">Dakikalar içinde kurulum</div>
          <p className="dg-tile__text">Karmaşık eğitim yok; hemen kullanmaya başlayın.</p>
        </Reveal>

        <Reveal className="dg-tile dg-tile--light span-4" y={20}>
          <div className="dg-tile__icon">
            <Icon name="users" />
          </div>
          <div className="dg-tile__eyebrow">Ekip yönetimi</div>
          <div className="dg-tile__title">Personel, yetki ve komisyonlar kontrol altında</div>
          <p className="dg-tile__text">Kim ne yaptı, ne kazandı; şeffaf ve kayıtlı. Şubeler arası yönetim tek panelden.</p>
        </Reveal>

        <Reveal className="dg-tile dg-tile--dark span-2" y={20} delay={0.05}>
          <div className="dg-tile__icon">
            <Icon name="heart" />
          </div>
          <div className="dg-tile__title">Yanınızdayız</div>
          <p className="dg-tile__text">WhatsApp’tan gerçek insanlarla hızlı destek.</p>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ==========================================================================
 * Metrikler
 * ========================================================================== */
const METRICS: { to: number; suffix: string; label: string; decimals?: number }[] = [
  { to: 2500, suffix: '+', label: 'Yönetilen organizasyon' },
  { to: 180, suffix: '+', label: 'Mutlu işletme' },
  { to: 99.9, decimals: 1, suffix: '%', label: 'Çalışma süresi' },
  { to: 7, suffix: '/24', label: 'WhatsApp destek' },
];
const Metrics: React.FC = () => (
  <section className="dg-section dg-section--tight">
    <div className="dg-container">
      <div className="dg-metrics">
        {METRICS.map((m, i) => (
          <Reveal key={m.label} delay={i * 0.08} y={16}>
            <div className="dg-metric__num">
              <CountUp to={m.to} suffix={m.suffix} decimals={m.decimals || 0} />
            </div>
            <div className="dg-metric__label">{m.label}</div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ==========================================================================
 * Mobil
 * ========================================================================== */
const MobileSection: React.FC = () => (
  <section className="dg-section dg-mobile" id="mobil">
    <div className="dg-container">
      <div className="dg-mobile__grid">
        <Reveal y={24}>
          <motion.div
            initial={{ opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, ease: [0.21, 0.5, 0.2, 1] }}
          >
            <div className="dg-phone">
              <div className="dg-phone__screen">
                <img className="dg-phone__shot" src={SHOTS.mobil} alt="Düğüncüm mobil — rezervasyon takvimi" />
              </div>
            </div>
          </motion.div>
        </Reveal>

        <Reveal delay={0.1} y={24}>
          <span className="dg-eyebrow">Mobil uygulama</span>
          <h2 className="dg-h2" style={{ marginTop: 12 }}>
            İşiniz nerede olursanız olun yanınızda.
          </h2>
          <p className="dg-lead" style={{ marginTop: 18 }}>
            Rezervasyonları, tahsilatları ve bildirimleri cebinizden anlık takip edin.
          </p>
          <div className="dg-mobile__note">
            <Icon name="phone" size={17} /> Mobil uygulama Pro ve Max paketlerinde yer alır.
          </div>
          <div className="dg-mobile__badges">
            <a className="dg-store" href={APP_STORE_URL} target="_blank" rel="noreferrer">
              <AppleIcon />
              <div>
                <span>App Store’dan indir</span>
                <b>App Store</b>
              </div>
            </a>
            {PLAY_STORE_URL ? (
              <a className="dg-store" href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                <PlayIcon />
                <div>
                  <span>Google Play’den indir</span>
                  <b>Google Play</b>
                </div>
              </a>
            ) : (
              <span className="dg-store dg-store--soon" aria-disabled="true">
                <PlayIcon />
                <div>
                  <span>Google Play</span>
                  <b>Pek Yakında</b>
                </div>
              </span>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ==========================================================================
 * Fiyatlandırma (WhatsApp'a yönlendirir)
 * ========================================================================== */
const Pricing: React.FC = () => (
  <section className="dg-section dg-pricing" id="fiyatlandirma">
    <div className="dg-container">
      <div className="dg-section__head">
        <span className="dg-eyebrow">Fiyatlandırma</span>
        <h2 className="dg-h2">İşletmenize uygun paketi seçin.</h2>
        <p className="dg-lead">Ödeme derdi yok. Beğendiğiniz paket için WhatsApp’tan yazın; kurulumu birlikte başlatalım.</p>
      </div>

      <div className="dg-plans">
        {PLANS.map((plan, i) => (
          <Reveal key={plan.id} delay={i * 0.08} y={22}>
            <div className={`dg-plan ${plan.highlight ? 'dg-plan--highlight' : ''}`}>
              {plan.badge && <span className="dg-plan__badge">{plan.badge}</span>}
              <h3 className="dg-plan__name">{plan.name}</h3>
              <p className="dg-plan__tagline">{plan.tagline}</p>

              <div className="dg-plan__price">
                {plan.priceSuffix ? (
                  <>
                    <b>{plan.price}</b>
                    <em>{plan.priceSuffix}</em>
                  </>
                ) : (
                  <b>{plan.price}</b>
                )}
              </div>
              <div className="dg-plan__period">{plan.period}</div>

              <div className="dg-plan__divider" />

              <ul className="dg-plan__features">
                {plan.features.map((f) => (
                  <li key={f.text} className={`dg-plan__feature ${f.included ? 'dg-plan__feature--yes' : 'dg-plan__feature--no'}`}>
                    <i>{f.included ? <Icon name="check" size={16} /> : <Icon name="minus" size={16} />}</i>
                    {f.text}
                  </li>
                ))}
              </ul>

              {plan.id === 'free' ? (
                <a className="dg-btn dg-btn--block dg-btn--primary" href={REGISTER_URL}>
                  {plan.ctaLabel}
                </a>
              ) : (
                <a
                  className={`dg-btn dg-btn--block ${plan.highlight ? 'dg-btn--wa' : 'dg-btn--primary'}`}
                  href={whatsappLink(`${plan.name} (${plan.price}${plan.priceSuffix || ''} ${plan.period || ''})`)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <WhatsAppIcon size={18} /> {plan.ctaLabel}
                </a>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      <p className="dg-pricing__note">Kurulum tüm paketlerde ücretsizdir. Fiyatlara KDV dahil değildir · WhatsApp: {WHATSAPP_DISPLAY}</p>
    </div>
  </section>
);

/* ==========================================================================
 * SSS
 * ========================================================================== */
const FAQS = [
  {
    q: 'Düğüncüm’ü kullanmaya nasıl başlarım?',
    a: 'Fiyatlandırma bölümünden size uygun paketi seçip WhatsApp’tan bize yazmanız yeterli. Hesabınızı hazırlıyor, kısa bir tanıtımla kullanıma açıyoruz. Başlangıç paketi 1 ay tamamen ücretsizdir.',
  },
  {
    q: 'Ücretsiz paket neleri kapsıyor?',
    a: 'Başlangıç paketi 1 ay boyunca ücretsizdir ve web yönetim paneli üzerinden çalışır. Bu pakette mobil uygulama erişimi bulunmaz; mobil uygulama Pro ve Max paketlerinde yer alır.',
  },
  {
    q: 'Pro ve Max arasındaki fark nedir?',
    a: 'Pro paket aylık 499₺’dir, 1 kullanıcı hesabı içerir ve SMS servislerini kapsamaz. Max paket aylık 999₺’dir, kullanıcı sınırı yoktur ve SMS bildirim servisleri dahildir. Her iki pakette de mobil uygulama erişimi vardır.',
  },
  {
    q: 'Ödeme nasıl yapılıyor?',
    a: 'Sitede otomatik ödeme yönlendirmesi yoktur. Paket seçiminizi WhatsApp üzerinden iletiyorsunuz; ödeme ve kurulumu size en uygun şekilde birlikte ilerletiyoruz.',
  },
  {
    q: 'Verilerim güvende mi?',
    a: 'Evet. Tüm verileriniz şifreli ve düzenli olarak yedeklenen bir altyapıda saklanır. Yalnızca yetkilendirdiğiniz kullanıcılar erişebilir.',
  },
  {
    q: 'Taahhüt zorunlu mu?',
    a: 'Hayır. Paketler aylık kullanıma göre planlanmıştır; dilediğiniz zaman yükseltebilir veya değiştirebilirsiniz.',
  },
];
const Faq: React.FC = () => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="dg-section dg-faq" id="sss">
      <div className="dg-container">
        <div className="dg-section__head">
          <span className="dg-eyebrow">Sorular</span>
          <h2 className="dg-h2">Sıkça sorulan sorular.</h2>
        </div>
        <div className="dg-faq__list">
          {FAQS.map((item, i) => (
            <div key={i} className={`dg-faq__item ${open === i ? 'is-open' : ''}`}>
              <button className="dg-faq__q" onClick={() => setOpen(open === i ? null : i)}>
                {item.q}
                <span>+</span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <p className="dg-faq__a">{item.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ==========================================================================
 * CTA
 * ========================================================================== */
const Cta: React.FC = () => (
  <section className="dg-cta">
    <div className="dg-container">
      <Reveal y={20}>
        <h2 className="dg-cta__title">İşletmenizi bugün akışa alın.</h2>
        <p className="dg-cta__sub">1 ay ücretsiz deneyin. Kurulumdan kullanıma kadar WhatsApp’tan yanınızdayız.</p>
        <div className="dg-cta__actions">
          <a className="dg-btn dg-btn--wa dg-btn--lg" href={whatsappLink()} target="_blank" rel="noreferrer">
            <WhatsAppIcon size={18} /> WhatsApp’tan başlayın
          </a>
          <a className="dg-link" href={ADMIN_URL}>
            Yönetim paneli <Chevron />
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ==========================================================================
 * Footer
 * ========================================================================== */
const Footer: React.FC = () => (
  <footer className="dg-footer">
    <div className="dg-container">
      <div className="dg-footer__top">
        <div>
          <div className="dg-footer__brand">
            <img src={LOGO} alt="Düğüncüm" />
          </div>
          <p className="dg-footer__about">Düğün organizasyonu işletmeleri için rezervasyon, finans ve müşteri yönetimini tek panelde birleştiren sade SaaS çözümü.</p>
        </div>
        <div className="dg-footer__col">
          <h4>Ürün</h4>
          <a href="#genel">Genel Bakış</a>
          <a href="#ozellikler">Özellikler</a>
          <a href="#mobil">Mobil Uygulama</a>
          <a href="#fiyatlandirma">Fiyatlandırma</a>
        </div>
        <div className="dg-footer__col">
          <h4>Kurumsal</h4>
          <a href={ADMIN_URL}>Yönetim Paneli</a>
          <a href="#sss">SSS</a>
          <a href={whatsappLink()} target="_blank" rel="noreferrer">
            WhatsApp Destek
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </div>
        <div className="dg-footer__col">
          <h4>Yasal</h4>
          <a href="/privacy-and-security">Gizlilik & Güvenlik</a>
          <a href="/distance-sales-contract">Mesafeli Satış</a>
          <a href="/cancel-refund">İptal & İade</a>
        </div>
      </div>
      <div className="dg-footer__bottom">
        <span>© {new Date().getFullYear()} Düğüncüm. Tüm hakları saklıdır.</span>
        <span>WhatsApp: {WHATSAPP_DISPLAY}</span>
      </div>
    </div>
  </footer>
);

/* ==========================================================================
 * Uygulama kabuğu
 * ========================================================================== */
const MarketingApp: React.FC = () => {
  useEffect(() => {
    // Metronic açılış ekranını (#splash-screen) kapat; yalnızca admin uygulaması
    // gizlediğinden tanıtım sitesinde manuel kapatıyoruz.
    const splash = document.getElementById('splash-screen');
    if (splash) splash.style.setProperty('display', 'none');
    document.body.classList.remove('page-loading');
    document.documentElement.classList.add('dg-smooth');
    return () => document.documentElement.classList.remove('dg-smooth');
  }, []);

  return (
    <div className="dg-site">
      <Nav />
      <Hero />
      <Statement />
      <PinnedFeatures />
      <Bento />
      <Metrics />
      <MobileSection />
      <Pricing />
      <Faq />
      <Cta />
      <Footer />

      <a className="dg-wa-float" href={whatsappLink()} target="_blank" rel="noreferrer" aria-label="WhatsApp ile iletişime geç">
        <WhatsAppIcon size={30} />
      </a>
    </div>
  );
};

export default MarketingApp;
